import { base44 } from '@/api/base44Client';

// Card variants generated in the browser and stored as WebP on the app's own storage.
// 400px serves 1x cards, 800px serves the large/retina layouts.
const CARD_WIDTHS = { small: 400, large: 800 };
const CARD_QUALITY = 0.75;

// How wide each card really is rendered, so the browser can pick the right file.
export const CARD_SIZES = {
  default: '(min-width: 1024px) 400px, 300px',
  compact: '96px',
  deal: '(max-width: 768px) 90vw, 192px',
  suggestion: '(min-width: 768px) 300px, 85vw',
};

const baseNameOf = (file) => (file.name || 'image').replace(/\.[^.]+$/, '') || 'image';

function loadImage(src, useCors) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    if (useCors) img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Bild konnte nicht geladen werden'));
    img.src = src;
  });
}

async function encode(img, maxWidth, quality, filename) {
  const scale = Math.min(1, maxWidth / img.naturalWidth);
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(img.naturalWidth * scale);
  canvas.height = Math.round(img.naturalHeight * scale);
  canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
  const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/webp', quality));
  if (!blob) throw new Error('Bild konnte nicht konvertiert werden');
  const { file_url } = await base44.integrations.Core.UploadFile({
    file: new File([blob], filename, { type: 'image/webp' }),
  });
  return file_url;
}

async function buildVariants(img, name) {
  const [image_small, og_image] = await Promise.all([
    encode(img, CARD_WIDTHS.small, CARD_QUALITY, `${name}-400.webp`),
    encode(img, CARD_WIDTHS.large, CARD_QUALITY, `${name}-800.webp`),
  ]);
  return { image_small, og_image };
}

// Full-size image from a freshly picked file (object URL — no CORS involved).
export async function uploadWebp(file, maxWidth = 1920, quality = 0.82) {
  const objectUrl = URL.createObjectURL(file);
  try {
    const img = await loadImage(objectUrl, false);
    return await encode(img, maxWidth, quality, `${baseNameOf(file)}.webp`);
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

// Both card variants from a freshly picked file — no CORS risk.
export async function generateVariantsFromFile(file) {
  const objectUrl = URL.createObjectURL(file);
  try {
    const img = await loadImage(objectUrl, false);
    return await buildVariants(img, baseNameOf(file));
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

// Both card variants from an image that is already stored (media host allows
// cross-origin reads). Throws on failure so callers never store wrong values.
export async function generateVariantsFromUrl(url) {
  const img = await loadImage(url, true);
  return await buildVariants(img, 'card');
}

// srcSet built only from variants that actually exist, each with its true width.
export function variantSrcSet(record) {
  const candidates = [];
  if (record?.image_small) candidates.push(`${record.image_small} 400w`);
  if (record?.og_image) candidates.push(`${record.og_image} 800w`);
  return candidates.length > 0 ? candidates.join(', ') : undefined;
}